import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { centrifugeService } from "../axiosBase/centrifugeService";

/**
 * Subscribe to a Centrifuge channel and receive its publications as local state.
 *
 * The hook taps into the global centrifugeService publication bus, so it works
 * for both:
 *   - Auto-channels  (already subscribed from the WS JWT on connect)
 *   - Custom channels (the hook creates the subscription and tears it down on unmount)
 *
 * Note: global event routing (data.type → Redux wsEvents) is handled centrally
 * by useWebSocketManager, so this hook only manages local component state.
 *
 * @param {string | null} channel — Centrifuge channel name.
 *   Pass null / undefined to skip (useful for conditional subscriptions).
 *
 * @returns {{
 *   isSubscribed:      boolean,
 *   latestPublication: object | null,
 *   publish:           (data: object) => Promise<void>,
 * }}
 *
 * Examples:
 *   // Listen to an auto-channel (subscribed from JWT)
 *   const { latestPublication } = useCentrifugeChannel("prod_user_96");
 *
 *   // Subscribe to a custom channel
 *   const { isSubscribed, publish } = useCentrifugeChannel("orders#store123");
 *   publish({ action: "acknowledge", orderId: "abc" });
 */
export const useCentrifugeChannel = (channel) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [latestPublication, setLatestPublication] = useState(null);
  const { connectionStatus } = useSelector((state) => state.webSocketReducer);

  // Track whether THIS hook instance created the subscription so we know
  // whether to unsubscribe on unmount (auto-channels must not be removed).
  const createdSubRef = useRef(false);
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    if (!channel || connectionStatus !== "connected") return;

    // Only create a subscription if it doesn't already exist (auto-channels
    // are subscribed by centrifugeService during connect).
    if (!centrifugeService.isSubscribedTo(channel)) {
      centrifugeService.subscribe(channel);
      createdSubRef.current = true;
      setIsSubscribed(true);
    } else {
      setIsSubscribed(true);
    }

    // Tap into the global publication bus filtered by this channel
    const unregister = centrifugeService.onPublication(
      (publishedChannel, data) => {
        if (publishedChannel === channel) {
          setLatestPublication(data);
        }
      },
    );

    return () => {
      unregister();
      // Only unsubscribe channels that this hook created
      if (createdSubRef.current) {
        centrifugeService.unsubscribe(channelRef.current);
        createdSubRef.current = false;
      }
      setIsSubscribed(false);
    };
  }, [channel, connectionStatus]);

  const publish = useCallback(
    (data) => centrifugeService.publish(channel, data),
    [channel],
  );

  return { isSubscribed, latestPublication, publish };
};
